import * as React from 'react'
import { CloudinaryVideo as CloudinaryVideoType } from '../../types'
import { AudioButton, PlaybackButton } from './Controls'
import { VideoWrapper } from './styled'
import { useViewportSize } from '../../utils'

const { useRef, useEffect, useState } = React
const BASE_URL = 'https://res.cloudinary.com/spinelli-kilcollin/video/upload'

const fallbackSizes = [720, 1200, 1600]

interface VideoElementProps {
  video: CloudinaryVideoType
  muted: boolean
  poster: string
  playing: boolean | undefined
  setIsLoaded: (p: boolean) => void
  onPlay: () => void
}

const NormalVideo = ({
  playing,
  poster,
  muted,
  video,
  onPlay,
  setIsLoaded,
}: VideoElementProps) => {
  const { width: viewportWidth } = useViewportSize()
  const [src, setSrc] = useState<string | null>(null)

  const videoRef = useRef<HTMLVideoElement>(null)

  const bestSize =
    fallbackSizes.find((fs) => fs > viewportWidth) ?? fallbackSizes[2]

  useEffect(() => {
    setSrc(
      `https://res.cloudinary.com/spinelli-kilcollin/video/upload/c_scale,w_${bestSize}/${video.videoId}.mp4`,
    )
  }, [bestSize, video.videoId])

  useEffect(() => {
    if (!src || !videoRef.current) return

    const videoEl = videoRef.current
    const startAutoplayPromise = videoEl.play()
    if (startAutoplayPromise !== undefined) {
      startAutoplayPromise
        .then(() => setIsLoaded(true))
        .catch((error) => {
          console.warn('Playback failed', error)
        })
    }
  }, [src])

  useEffect(() => {
    if (!videoRef.current) return
    const paused = videoRef.current.paused
    if (paused && playing === true) videoRef.current.play()
    if (!paused && playing === false) videoRef.current.pause()
  }, [playing])

  return (
    <video
      ref={videoRef}
      onPlay={onPlay}
      poster={poster}
      muted={muted}
      loop
      playsInline
      src={src || ''}
    />
  )
}

interface CloudinaryVideoProps {
  video: CloudinaryVideoType
  enableAudio?: boolean
}

export const CloudinaryVideo = ({ video }: CloudinaryVideoProps) => {
  const [muted, setMuted] = useState(true)
  const [playing, setPlaying] = useState<boolean | undefined>(undefined)
  const [loaded, setIsLoaded] = useState<boolean>(false)

  if (!video?.videoId) return null

  const { enableAudio, videoId } = video
  const poster = `${BASE_URL}/c_scale,w_1200/${videoId}.jpeg`

  const toggleAudio = () => setMuted(!muted)
  const togglePlaying = () => setPlaying(!playing)

  const handleOnPlay = () => {
    setPlaying(true)
  }

  return (
    <VideoWrapper $loaded={loaded}>
      <NormalVideo
        video={video}
        playing={playing}
        onPlay={handleOnPlay}
        setIsLoaded={setIsLoaded}
        poster={poster}
        muted={muted}
      />
      {enableAudio ? <AudioButton muted={muted} onClick={toggleAudio} /> : null}
      {<PlaybackButton playing={playing} onClick={togglePlaying} />}
    </VideoWrapper>
  )
}
